import { type HgSupportedLanguage } from '@smg-real-estate/domus';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';

import { getPolygon } from '@/api/fetchData';
import { getCulture } from '@/helpers/localizeLanguage';
import { LevelValue } from '@/models/iazi/common/enums';
import useGeoStore from '@/stores/useGeoStore';
import useMapStore from '@/stores/useMapStore';
import { EventSource, MapGeoLevel } from '@/types';
import { captureAction } from '@smg-real-estate/browser-rum';

interface RouterLocationPushContext {
  updateMapAndData: (fetchDataFlag?: boolean, fetchMapFlag?: boolean) => Promise<void>;
}
const useAfterRouterLocationChange = (): RouterLocationPushContext => {
  const mapStore = useMapStore();
  const { searchLocation } = storeToRefs(useGeoStore());
  const { eventSource, polygon } = storeToRefs(mapStore);

  const { locale } = useI18n();

  const updateMap = async (id: number, level: number): Promise<void> => {
    const ploygonData = await getPolygon({
      levelId: id,
      level,
      culture: getCulture(locale.value as HgSupportedLanguage),
    });
    mapStore.setPolygon({
      current: ploygonData,
      previous: polygon.value.current,
    });
  };

  const updateData = async (id: number, level: number): Promise<void> => {
    await mapStore.fetchKpiData({
      level,
      levelId: id,
      culture: getCulture(locale.value as HgSupportedLanguage),
    });
  };

  const callFetchMapAndData = async (
    id: number,
    level: number,
    fetchDataFlag: boolean,
    fetchMapFlag: boolean,
  ) => {
    const promises = [];
    if (fetchMapFlag) promises.push(updateMap(id, level));
    if (fetchDataFlag) promises.push(updateData(id, level));
    await Promise.all(promises);
  };

  const updateMapAndData = async (fetchDataFlag = true, fetchMapFlag = true): Promise<void> => {
    if (searchLocation.value) {
      const iazi = searchLocation.value.iazi;
      if (iazi?.id && iazi.level) {
        await callFetchMapAndData(iazi.id, iazi.level, fetchDataFlag, fetchMapFlag);
      } else {
        captureAction('No iazi level and id found', {
          geoId: searchLocation.value.id,
        });
      }
    } else if ([EventSource.Reset, EventSource.Init].includes(eventSource.value)) {
      mapStore.setPolygon({
        current: null,
        previous: null,
      });
      mapStore.setCurrentLevel(MapGeoLevel.Country);
      fetchDataFlag && (await updateData(LevelValue.Country, LevelValue.Country));
    }
  };
  return { updateMapAndData };
};

export default useAfterRouterLocationChange;
